import { BryntumScheduler, BryntumSchedulerProps } from '@bryntum/scheduler-react';
import { Cancel, Delete, Download, LocalShipping, Reply, Restore, SettingsBackupRestore } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Grid, Select, MenuItem, CircularProgress, Typography } from '@mui/material';
import { FC, MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { DateScroller } from '../../Components/CommonInputs';
import { SlimFormHeader } from '../../Components/Core/SlimFormHeader';
import './Dispatches.scss';
import { HaulerManagementDispatchingView } from './Views/HaulerDispatching/HaulerManagementDispatchingView';
import { OrderDispatchingView } from './Views/OrderDispatching/OrderDispatchingView';
import {
    useArchiveDispatchBulkDeleteFromDatabaseMutation,
    useLazyGetDispatchOrderManagementCurrentMonthQuery,
    usePatchDispatchCancelMutation,
    usePatchDispatchRestoreMutation,
    useUpdateDispatchBulkCompleteMutation,
    useUpdateDispatchBulkRestartMutation,
    useUpdateDispatchBulkRecallMutation,
    useUpdateDispatchBulkReleaseMutation
} from '../../store/generated/generatedApi';
import { EventModel, Popup, StateProvider } from '@bryntum/scheduler';
import { ManagementEventBar, OrderDownloadModal } from './Components';
import { getHtml, getToolTipHeader, isInThePast, isNotOutOfDate } from './DispatchesHelper';
import { ConfirmationDialogWithInput } from '../../Components/Core/ConfirmDialog/ConfirmationDialogWithInput';
import { ConfirmDialog } from '../../Components/Core/ConfirmDialog/ConfirmDialog';
import { endOfDay, format, isValid, startOfDay } from 'date-fns';
import { convertYYYYMMDDToDate, easternToday } from '../../util';
import { RestoreDispatchesDialog } from './RestoreDispatchesDialog';
import { userHasPermissions, UserPermissionContext } from '../../Components/CoreLib/library';
import DoneIcon from '@mui/icons-material/Check';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { useAuth0 } from '@auth0/auth0-react';
import { WebhookConnector } from '../../Components/Webhook';
import { Toast } from '@bryntum/scheduler';

export interface IDispatchesProps {
    webhookConnector: WebhookConnector;
    setWebhookConnector: (webhookConnector: WebhookConnector) => void;
}

StateProvider.setup('local');

export const Dispatches: FC<IDispatchesProps> = (props) => {
    const [deleteDispatches] = useArchiveDispatchBulkDeleteFromDatabaseMutation();
    const [releaseDispatches] = useUpdateDispatchBulkReleaseMutation();
    const [recallDispatches] = useUpdateDispatchBulkRecallMutation();
    const [cancelDispatches] = usePatchDispatchCancelMutation();
    const [restoreDispatches] = usePatchDispatchRestoreMutation();
    const [completeDispatches] = useUpdateDispatchBulkCompleteMutation();
    const [restartDispatches] = useUpdateDispatchBulkRestartMutation();
    const { permissions } = useContext(UserPermissionContext);
    const { webhookConnector, setWebhookConnector } = props;

    const isUserAllowedToCreateAndEdit = userHasPermissions(['create:dispatch', 'edit:dispatch'], permissions);

    const [searchParams, setSearchParams] = useSearchParams();
    const dateParam = searchParams.get('date');
    const viewParam = searchParams.get('view');

    const [date, setDate] = useState(dateParam ? startOfDay(convertYYYYMMDDToDate(dateParam)) : easternToday());
    const [isHaulerView, setIsDriverView] = useState(viewParam !== null ? viewParam === 'true' : false);
    const [isEditEventOpen, setEditEventOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<EventModel>();

    const [eventsToCancel, setEventsToCancel] = useState<string[]>([]);
    const [isCancelMenuOpen, setIsCancelMenuOpen] = useState(false);

    const haulerSchedulerRef = useRef<BryntumScheduler>() as MutableRefObject<BryntumScheduler>;
    const orderSchedulerRef = useRef<BryntumScheduler>() as MutableRefObject<BryntumScheduler>;

    const [completeButtonDisabled, setCompleteButtonDisabled] = useState(true);
    const [restartButtonDisabled, setRestartButtonDisabled] = useState(true);
    const [releaseButtonDisabled, setReleaseButtonDisabled] = useState(true);
    const [recallButtonDisabled, setRecallButtonDisabled] = useState(true);
    const [selectAllButtonDisabled, setSelectAllButtonDisabled] = useState(false);
    const [deselectAllButtonDisabled, setDeselectAllButtonDisabled] = useState(true);

    const [selectedOrderSchedulerRefEvents, setSelectedOrderSchedulerRefEvents] = useState<EventModel[]>([]);
    const [selectedHaulerSchedulerRefEvents, setSelectedHaulerSchedulerRefEvents] = useState<EventModel[]>([]);

    const [selectedCompletedOrderSchedulerRefEvents, setSelectedCompletedOrderSchedulerRefEvents] = useState<EventModel[]>([]);
    const [selectedCompletedHaulerSchedulerRefEvents, setSelectedCompletedHaulerSchedulerRefEvents] = useState<EventModel[]>([]);

    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [dispatchIdsToDelete, setDispatchIdsToDelete] = useState<string[]>([]);

    const [getCurrentMonthDispatch, { data, isFetching }] = useLazyGetDispatchOrderManagementCurrentMonthQuery();
    const [daysWithData, setDaysWithData] = useState<string[]>([]);
    const [month, setMonth] = useState(date);
    const [isLoading, setIsLoading] = useState(false);

    const [counts, setCounts] = useState<{
        total: number;
        completed: number;
        released: number;
        needsReleased: number;
    }>({
        total: 0,
        completed: 0,
        released: 0,
        needsReleased: 0,
    });

    const { user } = useAuth0();

    const loadData = useCallback(
        (date: Date) => {
            const formattedDate = format(date, 'yyyy-MM');
            getCurrentMonthDispatch({ startDate: formattedDate });
        },
        [getCurrentMonthDispatch]
    );

    useEffect(() => {
        loadData(month);
    }, [month, loadData]);

    useEffect(() => {
        if (!isFetching) {
            setDaysWithData(data ?? []);
        }
    }, [data, isFetching]);

    const onMonthChange = (newMonth: Date) => {
        setDaysWithData([]);
        setMonth(newMonth);
    };

    const canModifyEvent = useCallback((event: any) => {
        let canModify = true;
        if (isInThePast(event.startDate) || event.isCompleted || event.isOrderInReview || !isUserAllowedToCreateAndEdit || event.canceledOn != null) {
            canModify = false;
        }

        return canModify;
    }, [isUserAllowedToCreateAndEdit]);

    const handleResetFilters = useCallback(() => {
        StateProvider.instance.clear();
        // refresh the page
        window.location.reload();
    }, []);

    const schedulerConfig = useMemo<BryntumSchedulerProps>(() => ({
        cls: 'triton-dispatch-scheduler',
        startDate: startOfDay(date),
        endDate: endOfDay(date),
        subGridConfigs: {
            locked: { resizable: true, width: 150 },
            normal: { flex: 1 },
        },
        // Multi sort works alongside grouping: the grouper is applied first, then every sorter in order.
        // Ctrl/Cmd + click a header to append a sorter, or use the header context menu > Multi sort.
        sortFeature: {
            multiSort: true,
        },
        eventTooltipFeature: false,
        barMargin: 1,
        stripeFeature: true,
        // Bryntum's own current-time line uses the device clock; useEasternCurrentTimeLine draws it in Eastern instead
        timeRangesFeature: {
            showCurrentTimeLine: false,
        },
        zoomOnMouseWheel: false,
        zoomOnTimeAxisDoubleClick: false,
        zoomKeepsOriginalTimespan: true,
        headerZoomFeature: false,
        timeAxisHeaderMenuFeature: false,
        viewPreset: {
            base: 'hourAndDay',
            defaultSpan: 24,
        },
        createEventOnDblClick: false,
        eventDragCreateFeature: {
            disabled: true,
        },
        multiEventSelect: true,
        eventMenuFeature: {
            items: {
                viewEvent: {
                    text: 'View',
                    icon: 'b-fa b-fa-fw b-fa-eye',
                    weight: 100,
                },
                releaseEvent: false,
                recallEvent: false,
                editEvent: {
                    text: 'Edit',
                    weight: 300,
                },
                cancelEvent: {
                    text: 'Cancel',
                    icon: 'b-fa b-fa-fw b-fa-ban',
                    weight: 400,
                },
                deleteEvent: {
                    text: 'Delete',
                    weight: 500,
                },
                cutEvent: false,
                copyEvent: false,
                unassignEvent: false,
            },
        },
        eventEditFeature: {
            items: {
                nameField: null,
                resourceField: null,
                startDateField: null,
                endDateField: null,
            },
        },
        scheduleMenuFeature: {
            items: {
                addEvent: false,
            },
        },
        listeners: {
            beforeSelectionChange: (event) => {
                if ((event as any)?.selected?.[0]?.data?.isCompleted || (event as any)?.selected?.[0]?.data?.isOrderInReview) {
                    return false;
                }
            },
            beforeEventDrag: ({ source, eventRecord, resourceRecord, eventRecords, assignmentRecords, domEvent }) => {
                return canModifyEvent(eventRecord);
            },
            beforeEventAdd: ({ source }) => {
                return isNotOutOfDate(source.startDate) && isUserAllowedToCreateAndEdit;
            },
            beforeEventDropFinalize: ({ source, context }) => {
                const assignments = context.dropData.assignments as any[];
                if (assignments.length > 0) {
                    const assignment = assignments[0].resourceRecord;
                    if (assignment.isOrderInReview) {
                        Toast.show('This Order is in review and cannot be modified.');
                        context.valid = false;
                        return;
                    }
                    if (assignment.canceledOn) {
                        Toast.show('This Order is canceled and cannot be modified.')
                        context.valid = false;
                        return;
                    }
                    context.valid = true;
                    return;
                }
                context.valid = false;
                return;
            },
            beforeEventResize: ({ source, eventRecord }) => {
                return canModifyEvent(eventRecord);
            },
            beforeEventDelete({source, eventRecords}) {
                if (isNotOutOfDate(source.startDate) && isUserAllowedToCreateAndEdit) {
                        const removedEvents = eventRecords as any[];
                        if (removedEvents.filter((x) => x.data.isCompleted || x.data.isOrderInReview).length > 0) {
                            return false;
                        }
                        const idsToDelete = removedEvents.map((removedEvent) => removedEvent.data.id);
                        deleteDispatches(idsToDelete);
                } else {
                    return false;
                }
            },
            beforeEventEdit({ source, eventRecord }) {
                const canEdit = canModifyEvent(eventRecord);

                if (canEdit) {
                    setSelectedEvent(eventRecord);
                    setEditEventOpen(true);
                }

                return false;
            },
        },
    }), [canModifyEvent, date, deleteDispatches, isUserAllowedToCreateAndEdit]);

    const managementEventRenderer = useCallback(({ eventRecord, resourceRecord, assignmentRecord, renderData }) => {
        if (!(eventRecord as any).released) {
            renderData.eventStyle = 'hollow';
        }
        if ((eventRecord as any).isOrderInReview) {
            renderData.eventStyle = 'plain';
        }
        renderData.style = 'font-weight: 800';

        return <ManagementEventBar key={assignmentRecord.id} eventRecord={eventRecord} resourceRecord={resourceRecord} />;
    }, []);

    //order management toggle left side
    const [orderConfig] = useState<BryntumSchedulerProps>({
        ...schedulerConfig,
        stateId: 'orderScheduler',
        eventRenderer: managementEventRenderer,
        onEventClick({ source, eventRecord, event }) {
            if (event.metaKey || event.ctrlKey || event.altKey) {
                return;
            }
            new Popup({
                draggable: true,
                forElement: event.target as any,
                header: { title: getToolTipHeader('order', (eventRecord as any)?.data, eventRecord), cls: (eventRecord as any).driverId ? 'driver' : 'broker' },
                html: getHtml(source.startDate, (eventRecord as any)?.data, orderSchedulerRef, eventRecord),
                closeAction: 'destroy',
                scrollAction: 'realign',
                closable: true,
                tools: {
                    edit: {
                        type: 'tool',
                        disabled: isInThePast(source.startDate) || !isUserAllowedToCreateAndEdit || (eventRecord as any).data.isOrderInReview || (eventRecord as any).data.canceledOn,
                        cls: 'b-icon b-icon-edit custom-edit-icon',
                        onClick: () => {
                            setSelectedEvent(eventRecord);
                            setEditEventOpen(true);
                        },
                    },
                },
            });
        },
        listeners: {
            ...schedulerConfig.listeners,
            eventSelectionChange({ selection }) {
                const uncompletedEvents = selection.filter((event) => !(event as any).data.isCompleted && !(event as any).data.isOrderInReview);
                const completedEvents = selection.filter((event) => (event as any).data.isCompleted);
                setSelectedOrderSchedulerRefEvents(uncompletedEvents);
                setSelectedCompletedOrderSchedulerRefEvents(completedEvents);
            },
        },
        groupFeature: {
            field: 'customerName',
            ascending: false,
        },
    });

    //Driver management toggle left side
    const [haulerConfig] = useState<BryntumSchedulerProps>({
        ...schedulerConfig,
        stateId: 'haulerScheduler',
        eventRenderer: managementEventRenderer,
        onEventClick({ source, eventRecord, event }) {
            if (event.metaKey || event.ctrlKey || event.altKey) {
                return;
            }
            new Popup({
                draggable: true,
                forElement: event.target as any,
                header: { title: getToolTipHeader('hauler', (eventRecord as any)?.data, eventRecord), cls: (eventRecord as any).driverId ? 'driver' : 'broker' },
                html: getHtml(source.startDate, (eventRecord as any)?.data, haulerSchedulerRef, eventRecord),
                closeAction: 'destroy',
                scrollAction: 'realign',
                closable: true,
                tools: {
                    edit: {
                        type: 'tool',
                        disabled: !isNotOutOfDate(date) || !isUserAllowedToCreateAndEdit || (eventRecord as any).data.isOrderInReview || (eventRecord as any).data.canceledOn,
                        cls: 'b-icon b-icon-edit custom-edit-icon',
                        onClick: () => {
                            setSelectedEvent(eventRecord);
                            setEditEventOpen(true);
                        },
                    },
                },
            });
        },
        listeners: {
            ...schedulerConfig.listeners,
            eventSelectionChange({ selection }) {
                const uncompletedEvents = selection.filter((event) => !(event as any).data.isCompleted && !(event as any).data.isOrderInReview);
                const completedEvents = selection.filter((event) => (event as any).data.isCompleted);
                setSelectedHaulerSchedulerRefEvents(uncompletedEvents);
                setSelectedCompletedHaulerSchedulerRefEvents(completedEvents);
            },
        },
        groupFeature: {
            field: 'haulerType',
            ascending: false,
        },
        sortFeature: {
            field: 'code',
            ascending: true,
            // Without this, the initial programmatic sort ignores the Code column's custom
            // comparator and falls back to a plain string sort.
            prioritizeColumns: true,
        },
    });

    useEffect(() => {
        let releasedEvents = [] as EventModel[];
        let unreleasedEvents = [] as EventModel[];
        let completedEvents = [] as EventModel[];

        releasedEvents = releasedEvents.concat(selectedOrderSchedulerRefEvents.filter((x) => (x as any).released && (x as any).canceledOn == null));
        releasedEvents = releasedEvents.concat(selectedHaulerSchedulerRefEvents.filter((x) => (x as any).released && (x as any).canceledOn == null));

        unreleasedEvents = unreleasedEvents.concat(selectedOrderSchedulerRefEvents.filter((x) => !(x as any).released && (x as any).canceledOn == null));
        unreleasedEvents = unreleasedEvents.concat(selectedHaulerSchedulerRefEvents.filter((x) => !(x as any).released && (x as any).canceledOn == null));

        completedEvents = completedEvents.concat(selectedCompletedOrderSchedulerRefEvents);
        completedEvents = completedEvents.concat(selectedCompletedHaulerSchedulerRefEvents);

        if (releasedEvents.length > 0 || unreleasedEvents.length > 0 || completedEvents.length > 0) {
            setDeselectAllButtonDisabled(false);
            if (releasedEvents.length > 0) {
                setRecallButtonDisabled(false);
                setCompleteButtonDisabled(false);
            } else {
                setRecallButtonDisabled(true);
                setCompleteButtonDisabled(true);
            }

            if (unreleasedEvents.length > 0) {
                setReleaseButtonDisabled(false);
            } else {
                setReleaseButtonDisabled(true);
            }

            if (completedEvents.length > 0) {
                setRestartButtonDisabled(false);
            } else {
                setRestartButtonDisabled(true);
            }

            if (isHaulerView) {
                if (selectedHaulerSchedulerRefEvents.length === (haulerSchedulerRef?.current?.instance?.events?.length ?? 0)) {
                    setSelectAllButtonDisabled(true);
                } else {
                    setSelectAllButtonDisabled(false);
                }
            } else {
                if (selectedOrderSchedulerRefEvents.length === (orderSchedulerRef?.current?.instance?.events?.length ?? 0)) {
                    setSelectAllButtonDisabled(true);
                } else {
                    setSelectAllButtonDisabled(false);
                }
            }
        } else {
            setDeselectAllButtonDisabled(true);
            setSelectAllButtonDisabled(false);
            setRecallButtonDisabled(true);
            setReleaseButtonDisabled(true);
            setCompleteButtonDisabled(true);
            setRestartButtonDisabled(true);
        }
    }, [isHaulerView, selectedHaulerSchedulerRefEvents, selectedOrderSchedulerRefEvents, selectedCompletedHaulerSchedulerRefEvents, selectedCompletedOrderSchedulerRefEvents]);

    const selectAll = useCallback(() => {
        if (isHaulerView) {
            const events = haulerSchedulerRef?.current.instance.events as any[];
            haulerSchedulerRef?.current?.instance.selectEvents(events.filter((event) => !event.data.isCompleted && !event.data.isOrderInReview && !event.data.canceledOn));
        } else {
            const events = orderSchedulerRef?.current.instance.events as any[];
            orderSchedulerRef?.current?.instance.selectEvents(events.filter((event) => !event.data.isCompleted && !event.data.isOrderInReview && !event.data.canceledOn));
        }
    }, [isHaulerView]);

    const deselectAll = useCallback(() => {
        haulerSchedulerRef?.current?.instance.clearEventSelection();
        orderSchedulerRef?.current?.instance.clearEventSelection();
        setSelectAllButtonDisabled(false);
        setDeselectAllButtonDisabled(true);
        setReleaseButtonDisabled(true);
        setRecallButtonDisabled(true);
    }, []);

    const handleCancelDispatches = useCallback(
        (reason: string) => {
            if (eventsToCancel.length > 0) {
                cancelDispatches({ canceledItems: eventsToCancel.map((id) => ({ id, reason })) });
            }

            if (eventsToCancel.length === 0) {
                setIsCancelMenuOpen(false);
            }

            if (isHaulerView) {
                haulerSchedulerRef?.current?.instance.clearEventSelection();
                setSelectedHaulerSchedulerRefEvents([]);
            } else {
                orderSchedulerRef?.current?.instance.clearEventSelection();
                setSelectedOrderSchedulerRefEvents([]);
            }
            setSelectedEvent(undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            cancelDispatches,
            eventsToCancel,
            isHaulerView,
            orderSchedulerRef,
            haulerSchedulerRef,
            orderSchedulerRef?.current?.instance,
            haulerSchedulerRef?.current?.instance,
        ]
    );

    const cancelSelectedEvents = useCallback(
        (selectedId?: string) => {
            let idsToUpdate = [] as string[];
            const updateDispatches = (ref: MutableRefObject<BryntumScheduler>) => {
                let updatedIds = [] as string[];
                const selectedEvents = ref.current.instance.selectedEvents;
                const filteredEvents = !!selectedId ? (ref.current.instance.events.filter((x) => x.id === selectedId) as EventModel[]) : selectedEvents;

                if (filteredEvents.length > 0) {
                    filteredEvents.forEach((event) => {
                        event?.id && updatedIds.push(event.id! as string);
                    });
                }
                return updatedIds;
            };

            if (isHaulerView) {
                if (haulerSchedulerRef?.current?.instance?.selectedEvents) {
                    idsToUpdate = idsToUpdate.concat(updateDispatches(haulerSchedulerRef));
                }
            } else {
                if (orderSchedulerRef?.current?.instance?.selectedEvents) {
                    idsToUpdate = updateDispatches(orderSchedulerRef);
                }
            }

            if (idsToUpdate.length > 0) {
                setIsCancelMenuOpen(true);
                setEventsToCancel(idsToUpdate);
            } else {
                setIsCancelMenuOpen(false);
                setEventsToCancel([]);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isHaulerView, orderSchedulerRef, haulerSchedulerRef, orderSchedulerRef?.current?.instance, haulerSchedulerRef?.current?.instance]
    );

    const completeSelectedEvents = useCallback(() => {

        let ref = isHaulerView ? haulerSchedulerRef : orderSchedulerRef;
        let readyToComplete = ref.current.instance.selectedEvents.filter(x => (x as any).released && !(x as any).isCompleted)
        let idsToUpdate = [] as string[];

        ref.current.instance.eventStore.beginBatch();
        readyToComplete.forEach((event) => {
            event?.id && idsToUpdate.push(event.id! as string);
            event.set('released', false);
            event.set('isComplete', true);
        });
        ref.current.instance.eventStore.endBatch();
        ref.current.instance.eventStore.commit();

        completeDispatches(idsToUpdate);

        ref.current.instance.clearEventSelection();
        if (isHaulerView) {
            setSelectedHaulerSchedulerRefEvents([]);
        } else {
            setSelectedOrderSchedulerRefEvents([]);
        }
        setSelectedEvent(undefined);

    }, [isHaulerView, completeDispatches]);

    const restartSelectedEvents = useCallback(() => {

        let ref = isHaulerView ? haulerSchedulerRef : orderSchedulerRef;
        let readyToRestart = ref.current.instance.selectedEvents.filter(x => (x as any).isCompleted);
        let idsToUpdate = [] as string[];

        ref.current.instance.eventStore.beginBatch();
        readyToRestart.forEach((event) => {
            event?.id && idsToUpdate.push(event.id! as string);
            event.set('released', false);
            event.set('isComplete', false);
        })
        ref.current.instance.eventStore.endBatch();
        ref.current.instance.eventStore.commit();

        restartDispatches(idsToUpdate);

        ref.current.instance.clearEventSelection();
        if (isHaulerView) {
            setSelectedHaulerSchedulerRefEvents([]);
        } else {
            setSelectedOrderSchedulerRefEvents([]);
        }
        setSelectedEvent(undefined);

    }, [isHaulerView, restartDispatches]);

    const releaseOrRecallSelectedEvents = useCallback(
        (release: boolean, selectedId?: string) => {
            let idsToUpdate = [] as string[];

            const updateDispatches = (ref: MutableRefObject<BryntumScheduler>) => {
                let updatedIds = [] as string[];
                const selectedEvents = ref.current.instance.selectedEvents;
                const filteredEvents = !!selectedId ? (ref.current.instance.events.filter((x) => x.id === selectedId) as EventModel[]) : selectedEvents;

                if (filteredEvents.length > 0) {
                    ref.current.instance.eventStore.beginBatch();
                    filteredEvents.forEach((event) => {
                        event?.id && updatedIds.push(event.id! as string);
                        event.set('released', release);
                    });
                    ref.current.instance.eventStore.endBatch();
                    ref.current.instance.eventStore.commit();
                }
                return updatedIds;
            };

            if (isHaulerView) {
                if (haulerSchedulerRef?.current?.instance?.selectedEvents) {
                    idsToUpdate = idsToUpdate.concat(updateDispatches(haulerSchedulerRef));
                }
            } else {
                if (orderSchedulerRef?.current?.instance?.selectedEvents) {
                    idsToUpdate = updateDispatches(orderSchedulerRef);
                }
            }

            if (release) {
                releaseDispatches(idsToUpdate);
            } else {
                recallDispatches(idsToUpdate);
            }

            if (isHaulerView) {
                haulerSchedulerRef?.current?.instance.clearEventSelection();
                setSelectedHaulerSchedulerRefEvents([]);
            } else {
                orderSchedulerRef?.current?.instance.clearEventSelection();
                setSelectedOrderSchedulerRefEvents([]);
            }
            setSelectedEvent(undefined);
        },
        [isHaulerView, recallDispatches, releaseDispatches]
    );

    const updateDate = useCallback(
        (updatedDate: Date) => {
            const newDate = new Date(updatedDate);
            if (isValid(newDate)) {
                if (webhookConnector) {
                    webhookConnector.stopConnection();
                    setWebhookConnector(new WebhookConnector(user?.sub ?? ''));

                    webhookConnector?.stopConnection();
                }
                const startDate = startOfDay(newDate);
                setDate(startDate);
                setSearchParams(createSearchParams({ date: format(startDate, 'yyyy-MM-dd'), view: isHaulerView.toString() }));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setSearchParams, isHaulerView, orderSchedulerRef, haulerSchedulerRef, orderSchedulerRef?.current?.instance, haulerSchedulerRef?.current?.instance]
    );
    

    useEffect(() => {
        const config = {
            processItems({ eventRecord, items }) {
                if (eventRecord.released) {
                    items.recallEvent = {
                        text: 'Recall',
                        icon: 'b-fa b-fa-fw b-fa-reply',
                        weight: 200,
                        disabled: eventRecord.isOrderInReview,
                        onItem({ eventRecord }) {
                            releaseOrRecallSelectedEvents(false, eventRecord.id);
                        },
                    };
                } else {
                    items.releaseEvent = {
                        text: 'Release',
                        icon: 'b-fa b-fa-fw b-fa-share',
                        weight: 200,
                        disabled: eventRecord.isOrderInReview,
                        onItem({ eventRecord }) {
                            releaseOrRecallSelectedEvents(true, eventRecord.id);
                        },
                    };
                }

                if (eventRecord.isOrderInReview) {
                    items.editEvent.disabled = true;
                    items.cancelEvent.disabled = true;
                    items.deleteEvent.disabled = true;
                }
            },
            items: {
                viewEvent: {
                    text: 'View',
                    icon: 'b-fa b-fa-fw b-fa-eye',
                    weight: 100,
                    onItem({ source, eventRecord, event }) {
                        new Popup({
                            draggable: true,
                            forElement: event.target as any,
                            header: { title: 'Dispatch Information', cls: (eventRecord as any).driverId ? 'driver' : 'broker' },
                            html: getHtml(source.startDate, (eventRecord as any)?.data, eventRecord),
                            closeAction: 'destroy',
                            scrollAction: 'realign',
                        });
                    },
                },
                releaseEvent: false,
                recallEvent: false,
                editEvent: {
                    text: 'Edit',
                    weight: 300,
                },
                cancelEvent: {
                    text: 'Cancel',
                    icon: 'b-fa b-fa-fw b-fa-ban',
                    weight: 400,
                    onItem({ eventRecord }) {
                        cancelSelectedEvents(eventRecord.id);
                    },
                },
                deleteEvent: {
                    text: 'Delete',
                    weight: 500,
                    onItem({ eventRecord }) {
                        setDispatchIdsToDelete([eventRecord.id]);
                        setShowDeleteConfirmationDialog(true);
                    },
                },
                cutEvent: false,
                copyEvent: false,
                unassignEvent: false,
            },
        };

        orderSchedulerRef?.current?.instance?.features?.eventMenu?.setConfig({
            disabled: !isNotOutOfDate(date) || !isUserAllowedToCreateAndEdit,
            ...config,
        });

        haulerSchedulerRef?.current?.instance?.features?.eventMenu?.setConfig({
            disabled: !isNotOutOfDate(date) || !isUserAllowedToCreateAndEdit,
            ...config,
        });
    }, [date, orderSchedulerRef, haulerSchedulerRef, orderSchedulerRef?.current?.instance, haulerSchedulerRef?.current?.instance, releaseOrRecallSelectedEvents, cancelSelectedEvents, isUserAllowedToCreateAndEdit, deleteDispatches, setShowDeleteConfirmationDialog, setDispatchIdsToDelete]);

    

    const [viewType, setViewType] = useState('Order Management');

    const handleViewChange = useCallback((event) => {
        deselectAll();
        setViewType(event.target.value);
        setIsDriverView(event.target.value === 'Driver Management');
        setSearchParams(createSearchParams({ date: format(date, 'yyyy-MM-dd'), view: (!isHaulerView).toString() }));
    }, [date, deselectAll, isHaulerView, setSearchParams]);

    return (
        <div style={{ height: 'calc(-72px + 100vh)' }}>
            <OrderDownloadModal
                open={showDownloadModal}
                onConfirm={() => setShowDownloadModal(false)}
                onClose={() => setShowDownloadModal(false)}
                defaultDate={date}
            />
            <ConfirmationDialogWithInput
                open={isCancelMenuOpen}
                onClose={() => {
                    setIsCancelMenuOpen(false);
                }}
                formLabel={'Please enter a cancelation reason.'}
                title={`Cancel Dispatch(es)`}
                titleIcon={<Cancel />}
                cancelText={'Cancel'}
                confirmText={'Save'}
                save={handleCancelDispatches}
                requireConfirmation={false}
            />
            <ConfirmDialog
                open={showDeleteConfirmationDialog}
                onClose={() => {
                    setShowDeleteConfirmationDialog(false);
                    setDispatchIdsToDelete([]);
                }}
                content={<Typography>Are you sure you want to delete this dispatch? This action cannot be undone.</Typography>}
                title="Delete Dispatch"
                titleIcon={<Delete />}
                cancelText="Cancel"
                confirmText="Delete"
                onConfirm={() => {
                    deleteDispatches(dispatchIdsToDelete);
                    setShowDeleteConfirmationDialog(false);
                    setDispatchIdsToDelete([]);
                }}
                requireConfirmation={false}
            />
            <RestoreDispatchesDialog
                open={showRestoreModal}
                onConfirm={(selectedDispatches) => {
                    const ids = selectedDispatches.map((x) => x.id);
                    restoreDispatches(ids).then(() => setShowRestoreModal(false));
                }}
                onClose={() => setShowRestoreModal(false)}
                startDate={date}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <SlimFormHeader
                    hideDividers
                    objectName='Dispatch'
                    permissionsTypeName={'dispatch'}
                    icon={<LocalShipping />}
                    title={'Dispatch'}
                    titleMaxWidth="60vw"
                    breadcrumbs={[{ label: 'Home', navLink: '/' }]}
                    entityNameSingular={'dispatch'}
                    additionalHeaderItems={
                        <>
                            <Grid item>
                                {isLoading &&
                                    <Grid container justifyContent="center" alignItems="stretch" sx={{ padding: '0px' }}>
                                        <Grid item>
                                            <CircularProgress id="loading-indicator" />
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                            <Grid item>
                                <Typography sx={{ minWidth: '150px', textAlign: "right" }}>Total: {counts.total}</Typography>
                                <Typography sx={{ minWidth: '150px', textAlign: "right" }}>Completed: {counts.completed}</Typography>
                                <Typography sx={{ minWidth: '150px', textAlign: "right" }}>Released: {counts.released}</Typography>
                                <Typography sx={{ minWidth: '150px', textAlign: "right" }}>Needs Released: {counts.needsReleased}</Typography>
                            </Grid>
                            <Grid item>
                                <ButtonGroup variant='contained' color='primary' orientation="vertical">
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        startIcon={<Restore />}
                                        onClick={() => setShowRestoreModal(true)}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || !isUserAllowedToCreateAndEdit}>
                                        Restore
                                    </Button>
                                    <Button
                                        startIcon={<Cancel sx={{ transform: 'scaleX(-1)' }} />}
                                        variant='contained'
                                        color='primary'
                                        onClick={() => cancelSelectedEvents()}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || deselectAllButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Cancel
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item>
                                <ButtonGroup variant='contained' color='primary' orientation="vertical">
                                    <Button
                                        startIcon={<Reply sx={{ transform: 'scaleX(-1)' }} />}
                                        variant='contained'
                                        color='primary'
                                        onClick={() => releaseOrRecallSelectedEvents(true)}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || releaseButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Release
                                    </Button>
                                    <Button
                                        startIcon={<Reply />}
                                        variant='contained'
                                        color='primary'
                                        onClick={() => releaseOrRecallSelectedEvents(false)}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || recallButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Recall
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item>
                                <ButtonGroup variant='contained' color='primary' orientation="vertical">
                                    <Button
                                        startIcon={<DoneIcon />}
                                        variant='contained'
                                        color='primary'
                                        onClick={completeSelectedEvents}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || completeButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Complete
                                    </Button>
                                    <Button
                                        startIcon={<RemoveDoneIcon />}
                                        variant='contained'
                                        color='primary'
                                        onClick={restartSelectedEvents}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || restartButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Restart
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item>
                                <ButtonGroup variant='contained' color='primary'>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={deselectAll}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || deselectAllButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Deselect All
                                    </Button>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={selectAll}
                                        size="small"
                                        disabled={!isNotOutOfDate(date) || selectAllButtonDisabled || !isUserAllowedToCreateAndEdit}>
                                        Select All
                                    </Button>
                                    <Button
                                        startIcon={<Download />}
                                        variant='contained'
                                        onClick={() => {
                                            setShowDownloadModal(true);
                                        }}
                                        size="small">
                                        Download
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item>
                                <Button
                                    startIcon={<SettingsBackupRestore />}
                                    variant='outlined'
                                    onClick={() => {
                                        handleResetFilters();
                                    }}
                                    size="small">
                                    Reset To Default
                                </Button>
                            </Grid>
                            <Grid item>
                                <Select value={viewType} onChange={handleViewChange}>
                                    <MenuItem value='Order Management'>Order Management</MenuItem>
                                    <MenuItem value='Driver Management'>Driver Management</MenuItem>
                                </Select>
                            </Grid>
                            <Grid item>
                                <DateScroller date={date} updateDate={updateDate} daysWithData={daysWithData} handleOnMonthChange={onMonthChange} />
                            </Grid>
                        </>
                    }
                />
                {isHaulerView === false ? (
                    <OrderDispatchingView
                        date={date}
                        isEditEventOpen={isEditEventOpen}
                        schedulerConfig={orderConfig}
                        schedulerRef={orderSchedulerRef}
                        selectedEvent={selectedEvent}
                        setEditEventOpen={setEditEventOpen}
                        setSelectedEvent={setSelectedEvent}
                        disabled={isInThePast(date) || !isUserAllowedToCreateAndEdit}
                        setIsLoading={setIsLoading}
                        webhookConnector={webhookConnector}
                        setCounts={setCounts}
                    />
                ) : (
                    <HaulerManagementDispatchingView
                        date={date}
                        isEditEventOpen={isEditEventOpen}
                        schedulerConfig={haulerConfig}
                        schedulerRef={haulerSchedulerRef}
                        selectedEvent={selectedEvent}
                        setEditEventOpen={setEditEventOpen}
                        setSelectedEvent={setSelectedEvent}
                        disabled={isInThePast(date) || !isUserAllowedToCreateAndEdit}
                        setIsLoading={setIsLoading}
                        webhookConnector={webhookConnector}
                        setCounts={setCounts}
                    />
                )}
            </Box>
        </div>
    );
};

